/**
 * Lookup Select Component
 *
 * Reusable dropdown component for selecting from lookup data with lazy loading.
 * - Shows selected item in edit mode without loading all data
 * - Lazy loads all options when dropdown opens
 * - Supports search/filter within dropdown
 * - Handles loading and error states
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useLazyLookupData, FetchConfig, LookupItem } from '../../hooks';
import './LookupSelect.css';
import { Loader } from '../Shared/Loader';

export interface LookupSelectProps {
  /**
   * API fetch configuration
   */
  fetchConfig: FetchConfig;

  /**
   * Currently selected value (ID)
   */
  value: number | null;

  /**
   * Callback when value changes
   */
  onChange: (value: number | null) => void;

  /**
   * Placeholder text
   */
  placeholder?: string;

  /**
   * Field label
   */
  label?: string;

  /**
   * Is field required?
   */
  required?: boolean;

  /**
   * Is field disabled?
   */
  disabled?: boolean;

  /**
   * Show error message
   */
  error?: string;

  /**
   * Additional CSS class
   */
  className?: string;

  /**
   * Maximum items to show in dropdown (for large datasets)
   */
  maxItemsInDropdown?: number;

  /**
   * Enable search/filter in dropdown
   */
  searchable?: boolean;

  /**
   * Show loading indicator in dropdown
   */
  showLoadingIndicator?: boolean;
}

const LookupSelect: React.FC<LookupSelectProps> = ({
  fetchConfig,
  value,
  onChange,
  placeholder = 'Select an option...',
  label,
  required = false,
  disabled = false,
  error,
  className = '',
  maxItemsInDropdown = 50,
  searchable = true,
  showLoadingIndicator = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Use lazy loading hook
  const {
    data,
    selectedItem,
    loading,
    error: hookError,
    hasLoadedAll,
    lazyLoadOnDropdownOpen,
    getItemById: _getItemById,
  } = useLazyLookupData(fetchConfig, value, {
    initialLoad: true, // Load selected item on mount
    cacheExpiresIn: 5 * 60 * 1000, // 5 minutes
  });

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchable || !searchTerm.trim()) {
      return data;
    }

    const term = searchTerm.toLowerCase();
    return data.filter((item: LookupItem) =>
      item.name.toLowerCase().includes(term) ||
      item.id.toString().includes(term)
    );
  }, [data, searchTerm, searchable]);

  // Limit items shown in dropdown
  const displayedItems = useMemo(() => {
    return filteredData.slice(0, maxItemsInDropdown);
  }, [filteredData, maxItemsInDropdown]);

  // Handle dropdown toggle
  const handleToggleDropdown = async () => {
    if (!isOpen) {
      setIsOpen(true);
      // Lazy load all records when dropdown opens
      if (!hasLoadedAll) {
        await lazyLoadOnDropdownOpen();
      }
    } else {
      setIsOpen(false);
    }
  };

  // Handle option selection
  const handleSelectOption = (id: number) => {
    onChange(id);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Handle clear selection
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setSearchTerm('');
  };

  // Get display text for selected item
  const displayText = selectedItem
    ? `${selectedItem.name} (ID: ${selectedItem.id})`
    : placeholder;

  // Combine errors
  const displayError = error || hookError;

  return (
    <div className={`lookup-select-wrapper ${className}`}>
      {label && (
        <label className="lookup-select-label">
          {label}
          {required && <span className="required">*</span>}
        </label>
      )}

      <div className="lookup-select-container" ref={containerRef}>
        {/* Select Button */}
        <button
          className={`lookup-select-button ${error ? 'input-error' : ''} ${isOpen ? 'open' : ''}`}
          onClick={handleToggleDropdown}
          disabled={disabled}
          type="button"
        >
          <span className="lookup-select-text">{displayText}</span>
          <span className="lookup-select-icon">
            {loading && showLoadingIndicator ? '⟳' : isOpen ? '▲' : '▼'}
          </span>
        </button>

        {/* Clear Button */}
        {value && !disabled && (
          <button
            className="lookup-clear-button"
            onClick={handleClear}
            type="button"
            title="Clear selection"
          >
            ✕
          </button>
        )}

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="lookup-dropdown-menu">
            {/* Search Input */}
            {searchable && (
              <div className="lookup-search-input-wrapper">
                <input
                  type="text"
                  className="lookup-search-input"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
                {searchTerm && (
                  <button
                    className="lookup-search-clear"
                    onClick={() => setSearchTerm('')}
                    type="button"
                  >
                    ✕
                  </button>
                )}
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="lookup-menu-message">
                <Loader size="sm" label="Loading options..." />
              </div>
            )}

            {/* Error State */}
            {hookError && (
              <div className="lookup-menu-message lookup-error">
                <span>Failed to load options</span>
                <small>{hookError}</small>
              </div>
            )}

            {/* Empty State */}
            {!loading && !hookError && data.length === 0 && (
              <div className="lookup-menu-message">
                <span>No options available</span>
              </div>
            )}

            {/* No Search Results */}
            {!loading && !hookError && data.length > 0 && displayedItems.length === 0 && (
              <div className="lookup-menu-message">
                <span>No results match "{searchTerm}"</span>
              </div>
            )}

            {/* Options List */}
            {!loading && !hookError && displayedItems.length > 0 && (
              <>
                <div className="lookup-menu-options">
                  {displayedItems.map((item: LookupItem) => {
                    // Use loose equality to handle both string and number types
                    const isSelected = value != null && value == item.id;
                    return (
                      <button
                        key={item.id}
                        className={`lookup-menu-option ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleSelectOption(item.id)}
                        type="button"
                      >
                        <span className="lookup-option-name">{item.name}</span>
                        <span className="lookup-option-id">ID: {item.id}</span>
                        {isSelected && (
                          <span className="lookup-option-checkmark">✓</span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Show more indicator */}
                {filteredData.length > maxItemsInDropdown && (
                  <div className="lookup-menu-more">
                    <small>Showing {displayedItems.length} of {filteredData.length} results</small>
                  </div>
                )}
              </>
            )}

            {/* Help text */}
            {!loading && !hasLoadedAll && data.length > 0 && (
              <div className="lookup-menu-hint">
                <small>
                  {value ? '✓ Selected item is loaded. Open dropdown to see all options.' : 'Dropdown loaded. Search to find more options.'}
                </small>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Error Message */}
      {displayError && (
        <span className="lookup-error-text">{displayError}</span>
      )}
    </div>
  );
};

export default LookupSelect;
